import { GetStaticPaths, GetStaticProps } from "next";
import ContentService from "@/src/ContentService";
import { IBreed, ISpecies } from "@/src/@types/contentful";
import { GetStaticPathsResult } from "next/types";
import { BreedDetailPageProps } from "@/src/pages/BreedDetailPage";
import { ContentRenderer } from "@/src/ContentRenderer";
import { ContentUtils } from "@/src/ContentUtils";
import { Utils } from "@/src/Utils";
import { PrivateSettings } from "@/src/config/PrivateSettings";
import { BlogUtils } from "@/src/BlogUtils";
import { resolveBreedSeoTitle } from "@/src/utils/breedTitles";

export const getStaticPaths: GetStaticPaths = async () => {
    const results: GetStaticPathsResult = {
        paths: [],
        fallback: "blocking"
    };

    return results;
};

export const getStaticProps: GetStaticProps<BreedDetailPageProps> = async context => {
    const species = (context.params?.species as string) ?? "";
    const speciesSlug = species.replace("-breeds", "");
    const breedSlug = context.params?.breed as string;

    if (!speciesSlug || !breedSlug) {
        return {
            notFound: true
        };
    }

    const contentContext = ContentService.createContentContextFromNextContext(context);
    // Needs only the slug, so it runs alongside the breed and species fetches; awaited once the page is known to exist (ENG-19528)
    const disabledInAlternateLocale = ContentUtils.isDisabledInAlternateLocale("breed", breedSlug, contentContext);
    const breedInfo = await ContentService.getEntryBySlug<IBreed>("breed", breedSlug, contentContext);

    if (!breedInfo) {
        return {
            notFound: true
        };
    }

    const speciesInfo = await ContentService.getEntryBySlug<ISpecies>("species", speciesSlug, ContentService.createContentContextFromNextContext(context));

    if (!speciesInfo || speciesInfo.fields.slug !== breedInfo.fields.species?.fields.slug) {
        return {
            notFound: true
        };
    }

    const props: BreedDetailPageProps = {
        name: breedInfo.fields.name,
        description: ContentRenderer.richTextToPlaintext(breedInfo.fields.description),
        traits: breedInfo.fields.traits,
        personalityRating: breedInfo.fields.personalityRating,
        personalityContent: breedInfo.fields.personalityDescription,
        healthRating: breedInfo.fields.healthRating,
        careRating: breedInfo.fields.careRating,
        careContent: breedInfo.fields.careDescription,
        conclusion: breedInfo.fields.moreInfo,
        height: breedInfo.fields.height.map(value => value.replace(/inches/gi, "")),
        weight: breedInfo.fields.weight.map(value => value.replace(/pounds/gi, "")),
        lifespan: breedInfo.fields.lifespan.map(value => value.replace(/years/gi, "")),
        speciesName: speciesInfo.fields.name,
        speciesSlug: speciesInfo.fields.slug,
        breedSlug: breedInfo.fields.slug,
        seoTitle: resolveBreedSeoTitle(breedInfo.fields.seoTitle, breedInfo.fields.name, speciesInfo.fields.name),
        removeHreflang: breedInfo.fields.removeHreflang,
        // Entry dates for the Article node's datePublished / dateModified (ENG-19525). Breeds have no authored publish date
        // and the Delivery API's sys has no firstPublishedAt (only createdAt / updatedAt), so creation is the nearest first-publish date
        publishTimestamp: breedInfo.sys.createdAt,
        updateTimestamp: breedInfo.sys.updatedAt,
        seoDescription: breedInfo.fields.seoDescription ?? BlogUtils.summarizeContent(breedInfo.fields.description, 160)
    };

    if (breedInfo.fields.healthIssues) {
        props.healthTraits = breedInfo.fields.healthIssues
            .filter(issue => !!issue) // Filter undefined values
            .map(issue => ContentUtils.convertBreedHealthIssueToBreedTrait(issue!));
    }

    if (breedInfo.fields.featuredImage?.fields.file.url) {
        props.imageURL = "https:" + breedInfo.fields.featuredImage?.fields.file.url;
    }

    // No hreflang alternate when this breed has no counterpart on the other site, as state/city/competitor already do (ENG-19528)
    if (await disabledInAlternateLocale) {
        props.removeHreflang = true;
    }

    return {
        props: Utils.cleanUndefinedValues(props),
        revalidate: PrivateSettings.DEFAULT_ISR_REVALIDATION_TIME_IN_SECONDS
    };
};

import DefaultPage from "@/src/pages/BreedDetailPage";
export default DefaultPage;
